import { apiSlice } from "./apiSlice";

export const userApiSlice = apiSlice.injectEndpoints({
  tagTypes: [
    "Profile",
    "UpdateProfile",
    "RankInfo",
    "ShareLink",
    "Affiliate",
    "CurrentCredit",
    "BankAccounts",
    "PlayerBanks",
    "PlayerGetOtp",
    "PlayerVerifyOtp",
    "VerifyIdentity",
    "SaveTrackingData",
    "GetReferralIncomeSummary",
    "GetReferralYesterdayIncomeSummary",
    "GetReferralLifetimeIncomeSummary",
    "GetReferralBonus",
    "GetReferralIncome",
    "GetReferralInviteeList",
  ],
  endpoints: (builder) => ({
    listAffiliate: builder.mutation({
      query: (body) => ({
        url: "/user/api/v1/listAffiliate",
        method: "POST",
        body,
      }),
      providesTags: ["Affiliate"],
    }),
    getProfile: builder.query({
      query: () => "/user/api/v1/profile",
      providesTags: ["Profile"],
    }),
    updateProfile: builder.mutation({
      query: (body) => ({
        url: "/user/api/v1/profile",
        method: "PUT",
        body,
      }),
      providesTags: ["UpdateProfile"],
      invalidatesTag: ["Profile"],
    }),
    getRankInfo: builder.query({
      query: () => "/user/api/v1/rankInfo",
      providesTags: ["RankInfo"],
    }),
    getShareLink: builder.query({
      query: () => "/user/api/v1/shareLink",
      providesTags: ["ShareLink"],
    }),
    getBankAccounts: builder.query({
      query: () => "/user/api/v1/listBankAccounts",
      providesTags: ["BankAccounts"],
    }),
    playerBanks: builder.mutation({
      query: (playerBank) => ({
        url: `/user/api/v1/addBankAccount`,
        method: "POST",
        body: playerBank,
      }),
      providesTags: ["PlayerBanks"],
      invalidatesTags: ["BankAccounts", "Profile"],
    }),
    playerGetOtp: builder.query({
      query: (channel) => ({
        url: `/user/api/v1/getOtp?channel=${channel}`,
      }),
      providesTags: ["GetOtp"],
    }),
    playerVerifyOtp: builder.query({
      query: ({ channel, otp }) => ({
        url: `/user/api/v1/verifyOtp?channel=${channel}&otp=${otp}`,
      }),
      providesTags: ["VerifyOtp"],
    }),
    verifyIdentity: builder.mutation({
      query: ({ identity }) => ({
        url: `/user/api/v1/verifyIdentity`,
        method: "POST",
        body: identity,
      }),
      providesTags: ["VerifyIdentity"],
    }),
    saveTrackingData: builder.mutation({
      query: (trackingData) => ({
        url: `/user/api/v1/saveTrackingData`,
        method: "POST",
        body: trackingData,
      }),
      providesTags: ["SaveTrackingData"],
    }),
    getNextVip: builder.query({
      query: () => ({
        url: `/user/api/v1/getNextVip`,
        method: "GET",
      }),
      providesTags: ["GetNextVip"],
    }),
    getReferralIncomeSummary: builder.query({
      query: () => ({
        url: `/referralbonus/api/v1/referralIncomeSummary`,
        method: "GET",
      }),
      providesTags: ["GetReferralIncomeSummary"],
    }),
    getReferralYesterdayIncomeSummary: builder.query({
      query: () => ({
        url: `/referralbonus/api/v1/referralYesterdayIncomeSummary`,
        method: "GET",
      }),
      providesTags: ["GetReferralYesterdayIncomeSummary"],
    }),
    getReferralLifetimeIncomeSummary: builder.query({
      query: () => ({
        url: `/referralbonus/api/v1/referralLifetimeIncomeSummary`,
        method: "GET",
      }),
      providesTags: ["GetReferralLifetimeIncomeSummary"],
    }),
    getReferralBonus: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/referralbonus/api/v1/referralBonus`,
        method: "GET",
        params: { startDate, endDate },
      }),
      providesTags: ["GetReferralBonus"],
    }),
    getReferralCommission: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/referralbonus/api/v1/referralCommission`,
        method: "GET",
        params: { startDate, endDate },
      }),
      providesTags: ["GetReferralIncome"],
    }),
    getReferralInviteeList: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/referralbonus/api/v1/referralInviteeListSummary`,
        method: "GET",
        params: { startDate, endDate },
      }),
      providesTags: ["GetReferralInviteeList"],
    }),
    getWithdrawalDesc: builder.query({
      query: () => ({
        url: `/user/api/v1/getWithdrawalDesc`,
        method: "GET",
      }),
      providesTags: ["GetWithdrawalDesc"],
    }),
    getDepositDesc: builder.query({
      query: () => ({
        url: `/user/api/v1/getDepositDesc`,
        method: "GET",
      }),
      providesTags: ["GetDepositDesc"],
    }),
    getMinCurrentCredit: builder.query({
      query: () => ({
        url: `/user/api/v1/getMinCurrentCredit`,
        method: "GET",
      }),
      providesTags: ["GetMinCurrentCredit"],
    }),
    // Player-initiated VIP upgrade: crosses one manual gate + auto-cascade
    // (Option A). Invalidates Profile so the UI reflects the new rank + balance,
    // and VipDetails so the eligibility check re-runs.
    manualUpgradeVip: builder.mutation({
      query: () => ({
        url: `/user/api/v1/upgradeVip`,
        method: "POST",
      }),
      invalidatesTags: ["Profile", "VipDetails"],
    }),
  }),
});

export const {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetRankInfoQuery,
  useGetShareLinkQuery,
  useGetBankAccountsQuery,
  usePlayerBanksMutation,
  useLazyPlayerGetOtpQuery,
  useLazyPlayerVerifyOtpQuery,
  useVerifyIdentityMutation,
  useSaveTrackingDataMutation,
  useGetNextVipQuery,
  useGetReferralIncomeSummaryQuery,
  useGetReferralYesterdayIncomeSummaryQuery,
  useGetReferralLifetimeIncomeSummaryQuery,
  useGetReferralCommissionQuery,
  useGetReferralBonusQuery,
  useGetReferralInviteeListQuery,
  useGetDepositDescQuery,
  useGetWithdrawalDescQuery,
  useGetMinCurrentCreditQuery,
  useManualUpgradeVipMutation,
} = userApiSlice;
